'use client';

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

declare global {
	interface Window {
		fcWidget: {
			init: (options: FreshChatInitOptions) => void;
			destroy: () => void;
			hide: () => void;
			show: () => void;
			on: (event: string, callback: () => void) => void;
			off: (event: string, callback: () => void) => void;
		};
	}
}

interface FreshChatInitOptions {
	token: string;
	host: string;
	widgetUuid: string;
}

interface FreshdeskChatBotProps {
	host: string;
	token: string;
	scriptId: string;
	scriptSrc: string;
	defaultWidgetUuid: string;
	leaguesWidgetUuid?: string;
	whiteListedPages: string[];
}

export const FreshdeskChatBot = ({
	host,
	token,
	scriptId,
	scriptSrc,
	defaultWidgetUuid,
	leaguesWidgetUuid,
	whiteListedPages
}: FreshdeskChatBotProps) => {
	const pathname = usePathname();

	const getWidgetUuid = () => {
		if (leaguesWidgetUuid && (window.location.hostname === 'pickleballleagues.com' || pathname?.startsWith('/leagues'))) {
			return leaguesWidgetUuid;
		}

		return defaultWidgetUuid;
	};

	function initFreshChat() {
		if (window.fcWidget) {
			window.fcWidget.init({
				token,
				host,
				widgetUuid: getWidgetUuid()
			});
		}
	}

	function destroyFreshChat() {
		if (window.fcWidget) {
			window.fcWidget.destroy();
		}
	}

	function loadFreshdeskScript() {
		if (!document.getElementById(scriptId)) {
			const script = document.createElement('script');
			script.id = scriptId;
			script.async = true;
			script.src = scriptSrc;
			script.onload = initFreshChat;
			document.head.appendChild(script);
		} else {
			initFreshChat();
		}
	}

	useEffect(() => {
		const isLeaguesPage = window.location.hostname === 'pickleballleagues.com';
		const isWhitelistedPage = pathname && whiteListedPages.some((page) => pathname.startsWith(page));

		if (isWhitelistedPage || isLeaguesPage) {
			destroyFreshChat();
			if (window.fcWidget) {
				const handleDestroyed = () => {
					window.fcWidget.off('widget:destroyed', handleDestroyed);
					initFreshChat();
				};
				window.fcWidget.on('widget:destroyed', handleDestroyed);
			} else {
				loadFreshdeskScript();
			}
		} else {
			destroyFreshChat();
		}
	}, [pathname]);

	return null;
};
